import React, { useMemo } from 'react';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from '../OrbitContext';
import { useOrbitL4Actions } from '../l4/OrbitL4ActionProvider';
import L4DemoPrompts from '../l4/demo/L4DemoPrompts';
import { ORBIT_L4_DEMO_SESSION_ID } from '../l4/demo/constants';
import { getContextualStarters, isStarterAllowed, withoutAskedStarters } from '../promptStarters';
import useOrbitStarters from '../useOrbitStarters';
import OrbitActionChip, { OrbitActionChipRow } from './OrbitActionChip';

/** The frame draws two (`4955:11275`, `4955:11276`); a third would not fit the 447 column. */
const MAX_FOLLOW_UPS = 2;

/**
 * div.action-chip-row `4955:11274`, under the newest answer's meta line. The wire carries no
 * follow-up prompt of its own, so the chips come from the same static contextual catalogue the idle
 * panel offers - now keyed on the cards the reader has on screen rather than on the route alone, and
 * with everything this conversation has already asked removed.
 *
 * Both halves are PLA-9563 §2: keyed on the route alone the row drew the same two prompts for a
 * whole visit ("Show me the engagement trend." / "Which segments need attention?"), and drew them
 * again directly under the answer to one of them. With nothing new left to suggest the row is
 * withheld, which is the honest state - a suggestion the user has already taken is not a suggestion.
 */
const OrbitFollowUps: React.FC = () => {
  const { t } = useMultiLanguageTranslation();
  const { ask, isAsking, askedQuestions, sessionId } = useOrbitContext();
  const { enabled: layer4 } = useOrbitL4Actions();
  const { screen, cards, grants } = useOrbitStarters();

  const chips = useMemo(
    () =>
      withoutAskedStarters(
        getContextualStarters(screen, cards).filter(starter => isStarterAllowed(starter, grants)),
        starter => t(starter.labelKey),
        askedQuestions
      ).slice(0, MAX_FOLLOW_UPS),
    [screen, cards, grants, askedQuestions, t]
  );

  if (layer4 && sessionId === ORBIT_L4_DEMO_SESSION_ID) {
    return <L4DemoPrompts />;
  }

  if (chips.length === 0) {
    return null;
  }

  return (
    <OrbitActionChipRow data-testid="orbit-follow-ups">
      {chips.map(starter => (
        <OrbitActionChip
          key={starter.id}
          label={t(starter.labelKey)}
          disabled={isAsking}
          testId={`orbit-follow-up-${starter.id}`}
          onSelect={() => ask(t(starter.labelKey))}
        />
      ))}
    </OrbitActionChipRow>
  );
};

export default OrbitFollowUps;
